import { config } from "@/src/config";
import * as Sentry from "@sentry/react-native";
import { getAuthSession } from "../Client";
import { persistor, store } from "../Store";
import {
  refreshAuthSuccess,
  authFailedToRefreshToken,
  authFailedNoRefreshToken,
} from "./State";
import { refreshToken } from "./refreshToken";
import { Platform } from "react-native";
import cuid from "cuid";

type RefreshAuthResult =
  | {
      authToken: string;
    }
  | undefined;

let pending: Promise<RefreshAuthResult> | null = null;

let refreshing = false;

export async function refreshAuth() {
  if (pending) return pending;

  if (refreshing) {
    await new Promise((resolve) => setTimeout(resolve, 10));
    return refreshAuth();
  }

  refreshing = true;
  pending = refreshTokenLock(() => _refreshAuth());

  return pending.finally(() => {
    pending = null;
    refreshing = false;
  });
}
async function _refreshAuth() {
  try {
    const isSharedDevice = getAuthSession()?.isSharedDevice;
    const res = isSharedDevice
      ? await refreshWithRetry(
          `${config.baseUrl}/storecast/oauth/token`,
          config.auth.oauth.clientId
        )
      : await refreshWithRetry(
          config.auth.oauth.serviceConfiguration.tokenEndpoint,
          config.auth.oauth.clientId
        );
    if (res.success) {
      store.dispatch(
        refreshAuthSuccess({
          oldRefreshToken: res.inputRefreshToken,
          authToken: res.authToken,
          idToken: res.idToken,
          refreshToken: res.refreshToken ?? null,
          expiresAt: res.expiresAt ?? null,
        })
      );
      if (!res.refreshToken) {
        Sentry.captureMessage("No refresh token returned from server");
      }
      await persistor.flush();
      return {
        authToken: res.authToken,
      };
    } else if (res.shouldBeLoggedOut) {
      if (res.inputRefreshToken) {
        Sentry.captureMessage("LOGOUT: Failed to refresh token");
        store.dispatch(authFailedToRefreshToken(res.inputRefreshToken));
      } else {
        Sentry.captureMessage(
          "LOGOUT: Failed to refresh token: no refresh token"
        );
        store.dispatch(authFailedNoRefreshToken());
      }
    }

    return undefined;
  } catch (e) {
    Sentry.captureException(e);
  }
}

function getCurrentRefreshToken() {
  const authSession = getAuthSession();
  return authSession?.refreshToken;
}

async function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function refreshWithRetry(
  tokenUrl: string,
  clientId: string,
  retriesLeft: number = 3
) {
  Sentry.addBreadcrumb({
    category: "refreshToken",
    message: `Refreshing token: Retries left: ${retriesLeft}`,
  });
  const currentRefreshTOken = getCurrentRefreshToken();
  if (!currentRefreshTOken) {
    return {
      success: false,
      shouldBeLoggedOut: true,
      logoutReason: "no refresh token",
      inputRefreshToken: null,
    } as const;
  }
  const res = await refreshToken(tokenUrl, clientId, currentRefreshTOken);
  Sentry.addBreadcrumb({
    category: "refreshToken",
    message: `Refresh token: ${res.success ? "success" : "failure"}`,
    data: {
      shouldBeLoggedOut: res.success ? false : res.shouldBeLoggedOut,
      success: res.success,
      logoutReason: res.success ? undefined : res.logoutReason,
      unique: cuid(),
    },
  });
  if (res.success || res.shouldBeLoggedOut) {
    return res;
  }
  if (retriesLeft > 0) {
    await wait(Math.max(1000, 5000 - retriesLeft * 1000));
    return refreshWithRetry(tokenUrl, clientId, retriesLeft - 1);
  }
  return res;
}

function getAbortSignal(ms: number) {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}
async function refreshTokenLock<T>(cb: () => Promise<T>): Promise<T> {
  if (Platform.OS === "web" && navigator?.locks) {
    return await navigator.locks.request(
      "refresh-token",
      {
        mode: "exclusive",
        signal: getAbortSignal(30000),
      },
      async () => {
        return await cb();
      }
    );
  } else {
    return cb();
  }
}
